import React, { useState, useEffect, useCallback } from "react";
import {
	Modal,
	Form,
	DatePicker,
	Input,
	Table,
	InputNumber,
	Typography,
	Popconfirm,
	Space,
	Button,
	Card,
	Row,
	Col,
	message
} from "antd";
import dayjs from "dayjs";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { FORMAT_DATE, TYPE_MODAL } from "../../../constants/constants";
import { consoleLogUtil } from "../../../utils/consoleLogUtil";
import { salaries_create, salaries_update, salary_employee_income } from "../../../services/apis";
import LoadingBase from "../../../components/LoadingBase";
import { formatInputNumber, formatNumber, parseInputNumber } from "../../../utils/funcHelper";
import { useSelector } from "react-redux";

const { TextArea } = Input;

const numericFields = [
	"working_days",
	"overtime_hours",
	"late_early_hours",
	"salary_base",
	"daily_salary",
	"overtime_pay",
	"meal_allowance",
	"makeup_support",
	"travel_allowance",
	"late_early_penalty",
	"service_bonus",
	"service_referral_bonus",
	"product_referral_bonus",
	"social_insurance",
	"training_deposit",
	"advance_payment"
];

const calcTotalIncome = (row) => {
	const sum =
		(Number(row.daily_salary) || 0) +
		(Number(row.overtime_pay) || 0) +
		(Number(row.meal_allowance) || 0) +
		(Number(row.makeup_support) || 0) +
		(Number(row.travel_allowance) || 0) +
		(Number(row.service_bonus) || 0) +
		(Number(row.service_referral_bonus) || 0) +
		(Number(row.product_referral_bonus) || 0)
		- (Number(row.late_early_penalty) || 0);
	return sum;
};

const calcNetSalary = (row) => {
	const total = calcTotalIncome(row);
	const deductions =
		(Number(row.social_insurance) || 0) +
		(Number(row.training_deposit) || 0) +
		(Number(row.advance_payment) || 0);
	return total - deductions;
};

const EditableCell = ({
	editing,
	dataIndex,
	title,
	inputType,
	record,
	children,
	...restProps
}) => {
	let inputNode;
	if (inputType === "number") {
		inputNode = (
			<InputNumber
				min={0}
				style={{ width: "100%" }}
				placeholder="0"
				controls={false}
				precision={2}
				formatter={formatInputNumber}
				parser={parseInputNumber}
			/>
		);
	} else {
		inputNode = <Input style={{ width: "100%" }} />;
	}

	return (
		<td {...restProps}>
			{editing ? (
				<Form.Item
					name={dataIndex}
					style={{ margin: 0 }}
					rules={
						dataIndex === "employee_name"
							? [{ required: true, message: `Nhập ${title}` }]
							: []
					}
				>
					{inputNode}
				</Form.Item>
			) : (
				children
			)}
		</td>
	);
};

const ModalAddSalary = ({ visible, onCancel, type, title, dataItem, is_locked }) => {
	const [formHeader] = Form.useForm(); // Form phần thông tin chung
	const [formRow] = Form.useForm();    // Form edit từng dòng
	const [data, setData] = useState([]);
	const [editingKey, setEditingKey] = useState("");
	const [loading, setLoading] = useState(false);
	const isEditing = (record) => record.key === editingKey;
	const userReducer = useSelector((state) => state.userReducer?.data);
	
	// Khởi tạo dữ liệu từ danh sách nhân viên
	useEffect(() => {
	  if (type === TYPE_MODAL.EDIT) {
	    if (dataItem) {
			// GÁN key duy nhất cho mỗi dòng
			const rows = (dataItem?.details || []).map((r, idx) => ({
				...r,
				key: r.id || r.salary_id || r.employee_id || idx + 1,
			  }));
			setData(rows);
			formHeader.resetFields();
			setEditingKey("");
			formHeader.setFieldsValue({
				salary_month: dataItem.batch?.salary_month ? dayjs(dataItem.batch?.salary_month, "YYYY-MM") : null,
				note: dataItem?.batch?.note || "",
				total_working_days: dataItem?.batch?.total_working_days || (dayjs().daysInMonth() - 2)
			});
		}
	  } else {
		setData([]);
		formHeader.resetFields();
		setEditingKey("");

	  }
	}, [visible, dataItem]);

	const edit = (record) => {
		formRow.setFieldsValue({ ...record });
		setEditingKey(record.key ?? record.employee_id);
	};

	const cancel = () => {
		setEditingKey("");
	};

	const save = async (key) => {
		try {
			const row = await formRow.validateFields();
			const newData = [...data];
			const index = newData.findIndex((item) => key === item.key);
			if (index > -1) {
				const item = newData[index];
				const merged = { ...item, ...row };
				// Recalc derived fields
				merged.total_income = calcTotalIncome(merged);
				merged.net_salary = calcNetSalary(merged);
				newData.splice(index, 1, merged);
				setData(newData);
				setEditingKey("");
			} else {
				// new row
				row.total_income = calcTotalIncome(row);
				row.net_salary = calcNetSalary(row);
				newData.push(row);
				setData(newData);
				setEditingKey("");
			}
		} catch (errInfo) {
			console.log("Validate Failed:", errInfo);
		}
	};

	const columns_add = [
		{
			title: "STT",
			width: 60,
			fixed: "left",
			editable: false,
			align: "center",
			render: (_, __, index) => index + 1

		},
		{
			title: "Nhân viên",
			dataIndex: "employee_name",
			width: 160,
			fixed: "left",
			editable: false
		},
		{ 
			title: "Chức vụ", 
			dataIndex: "position_name", 
			width: 100, 
			align: "center",
			editable: false
		},
		{ 
			title: "Lương cơ bản", 
			dataIndex: "salary_base", 
			width: 130, 
			editable: false,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div>
		},
		{ 
			title: "Ngày công", 
			dataIndex: "working_days", 
			width: 80, 
			align: "center",
			editable: true 
		},
		{ 
			title: "Tăng ca (h)", 
			dataIndex: "overtime_hours", 
			width: 80,
			align: "center", 
			editable: true 
		},
		{ 
			title: "Muộn/Sớm (h)", 
			dataIndex: "late_early_hours", 
			width: 100, 
			align: "center",
			editable: true 
		},
		{ 
			title: "Lương công nhật", 
			dataIndex: "daily_salary", 
			width: 120, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Tiền tăng ca", 
			dataIndex: "overtime_pay", 
			width: 110, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "PC ăn", 
			dataIndex: "meal_allowance", 
			width: 110, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Hỗ trợ trang điểm", 
			dataIndex: "makeup_support", 
			width: 120, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div>  
		},
		{ 
			title: "Công tác phí", 
			dataIndex: "travel_allowance", 
			width: 120, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Phạt muộn/sớm", 
			dataIndex: "late_early_penalty", 
			width: 110, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Tiền tua", 
			dataIndex: "service_bonus", 
			width: 120, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Bán DV (x0.01)", 
			dataIndex: "service_referral_bonus", 
			width: 120, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Bán SP (x0.01)", 
			dataIndex: "product_referral_bonus", 
			width: 120, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div>
		},
		{
			title: "Tổng thu nhập",
			dataIndex: "total_income",
			width: 120,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div>
		},
		{ 
			title: "BHXH", 
			dataIndex: "social_insurance", 
			width: 110, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Cọc đào tạo", 
			dataIndex: "training_deposit", 
			width: 130, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{ 
			title: "Tạm ứng", 
			dataIndex: "advance_payment", 
			width: 110, 
			editable: true,
			align: "center",
			render: (v) =><div style={{textAlign: 'right'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{
			title: "Thực nhận",
			dataIndex: "net_salary",
			width: 120,
			align: "center",
			render: (v) =><div style={{textAlign: 'right', color: 'green'}}> <strong>{formatNumber(v,0)}</strong> </div> 
		},
		{
			title: "Ghi chú",
			dataIndex: "note",
			width: 160,
			editable: true
		},
		{
			title: "Thao tác",
			dataIndex: "operation",
			fixed: "right",
			width: 90,
			render: (_, record) => {
				const editable = isEditing(record);
				return editable ? (
					<span>
						<Typography.Link onClick={() => save(record.key)} style={{ marginRight: 8 }}>
							Lưu
						</Typography.Link>
						<Popconfirm title="Huỷ chỉnh sửa?" onConfirm={cancel}>
							<a>Huỷ</a>
						</Popconfirm>
					</span>
				) : (
					<Typography.Link disabled={editingKey !== ""} onClick={() => edit(record)}>
						Sửa
					</Typography.Link>
				);
			}
		}
	];

	const columns_edit = columns_add.filter(col => col.dataIndex !== 'operation');

	const columns = columns_add;

	const mergedColumns = columns.map((col) => {
		if (!col.editable) return col;
		return {
			...col,
			onCell: (record) => ({
				record,
				inputType: numericFields.includes(col.dataIndex) ? "number" : "text",
				dataIndex: col.dataIndex,
				title: col.title,
				editing: isEditing(record)
			})
		};
	});

	// Tự động tính daily_salary = salary_base / working_days khi thay đổi working_days
    const handleRowValuesChange = (changedValues, allValues) => {
		const updates = {};
        if (Object.prototype.hasOwnProperty.call(changedValues, "working_days")) {
            const wd = Number(changedValues?.working_days || 0);
			const totalWorkingDays = formHeader.getFieldValue("total_working_days") || dayjs().daysInMonth() - 2;
            const base = Number((data.find(it => it.key === editingKey)?.salary_base) || 0);
            const daily = wd > 0 && totalWorkingDays > 0 ? (base/totalWorkingDays) * wd : 0;
			updates.daily_salary = Number(daily.toFixed(2));
            formRow.setFieldsValue({ daily_salary: Number(daily.toFixed(2)) });
        }

		// overtime_hours -> overtime_pay = hours * 30000
		if (Object.prototype.hasOwnProperty.call(changedValues, "overtime_hours")) {
			const hours = Number(changedValues?.overtime_hours || 0);
			const base = Number((data.find(it => it.key === editingKey)?.salary_base) || 0);
			const totalWorkingDays = formHeader.getFieldValue("total_working_days") || dayjs().daysInMonth() - 2;
			const payPerHour = Number((base / totalWorkingDays) / 8); // Lấy lương cơ bản chia cho số ngày công chuẩn, chia tiếp cho 8 giờ
			const pay = Math.max(0, Math.round(hours * 30000)); // Tạm tính 30k/giờ tăng ca
			updates.overtime_pay = pay;
			formRow.setFieldsValue({ overtime_pay: pay });
		}

		// late_early_hours -> late_early_penalty = hours * số tiền công mỗi giờ
		if (Object.prototype.hasOwnProperty.call(changedValues, "late_early_hours")) {
			const hours = Number(changedValues?.late_early_hours || 0);
			const base = Number((data.find(it => it.key === editingKey)?.salary_base) || 0);
			const totalWorkingDays = formHeader.getFieldValue("total_working_days") || dayjs().daysInMonth() - 2;
			const payPerHour = Number((base / totalWorkingDays) / 8);
			const penalty = Math.max(0, Math.round(hours * payPerHour));
			updates.late_early_penalty = penalty;
			formRow.setFieldsValue({ late_early_penalty: penalty });
		}

		// TÍNH LẠI total_income và net_salary sau mọi thay đổi
        const idx = data.findIndex(i => i.key === editingKey);
        if (idx > -1) {
            const merged = { ...data[idx], ...allValues, ...updates };
            merged.total_income = calcTotalIncome(merged);
            merged.net_salary = calcNetSalary(merged);

            const newData = [...data];
            newData.splice(idx, 1, merged);
            setData(newData);
        }
    };

	const handleChangeMonth = async (value) => {
		consoleLogUtil("Chọn tháng tính lương:", value ? value.format(FORMAT_DATE.MMYYYY) : "null");
		try {
			const params = {
				date_from: value ? dayjs(value).startOf("month").format(FORMAT_DATE.YYYYMMDD) : null,
				date_to: value ? dayjs(value).endOf("month").format(FORMAT_DATE.YYYYMMDD) : null
			};
			setLoading(true);
			const res = await salary_employee_income(params);
			consoleLogUtil("Kết quả lấy thu nhập nhân viên theo tháng:", res);
			if (res && res?.status === 1) {
				setData([...res.data?.data?.map((r, idx) => ({
					key: idx + 1,
					position_name: r.position_name ?? '',
					employee_id: r.employee_id,
					employee_name: r.employee_name,
					working_days: 0,
					overtime_hours: 0,
					late_early_hours: 0,
					salary_base: r.salary_base || 0,
					daily_salary: 0,
					overtime_pay: 0,
					meal_allowance: 0,
					makeup_support: 0,
					travel_allowance: 0,
					late_early_penalty: 0,
					service_bonus: r.service_income	|| 0,
					service_referral_bonus: r.service_consult_commission || 0,
					product_referral_bonus: r.product_consult_commission || 0,
					total_income: 0,
					social_insurance: 0,
					training_deposit: 0,
					advance_payment: 0,
					net_salary: 0,
					note: ""
				}))]);
			} else {
				setData([]);
				message.error(res?.msg || "Lấy dữ liệu thu nhập nhân viên thất bại.");
			}
		} catch (error) {
			message.error("Lấy dữ liệu thu nhập nhân viên thất bại.");
		} finally {
			setLoading(false);
		}
	}

	const handleSaveAll = async () => {
		if (editingKey) {
			message.warning("Vui lòng lưu dòng đang chỉnh sửa trước khi lưu toàn bộ.");
			return;
		}
		const headerValues = formHeader.getFieldsValue();
		if (!headerValues.salary_month) {
			message.warning("Chọn tháng tính lương.");
			return;
		}
		if (headerValues.total_working_days == null) {
			message.warning("Nhập số ngày công trong tháng.");
			return;
		}
		if (type === TYPE_MODAL.ADD) {
			try {
				const payload = {
					salary_month: headerValues.salary_month.format("YYYY-MM"),
					note: headerValues.note?.trim() || "",
					total_working_days: Number(headerValues.total_working_days || 0),
					created_by: userReducer?.user_info?.id || null,
					employees: data.map(r => ({
						employee_id: r.employee_id,
						working_days: Number(r.working_days || 0),
						overtime_hours: Number(r.overtime_hours || 0),
						late_early_hours: Number(r.late_early_hours || 0),
						salary_base: Number(r.salary_base || 0),
						daily_salary: Number(r.daily_salary || 0),
						overtime_pay: Number(r.overtime_pay || 0),
						meal_allowance: Number(r.meal_allowance || 0),
						makeup_support: Number(r.makeup_support || 0),
						travel_allowance: Number(r.travel_allowance || 0),
						late_early_penalty: Number(r.late_early_penalty || 0),
						service_bonus: Number(r.service_bonus || 0),
						service_referral_bonus: Number(r.service_referral_bonus || 0),
						product_referral_bonus: Number(r.product_referral_bonus || 0),
						total_income: Number(calcTotalIncome(r)),
						social_insurance: Number(r.social_insurance || 0),
						training_deposit: Number(r.training_deposit || 0),
						advance_payment: Number(r.advance_payment || 0),
						net_salary: Number(calcNetSalary(r)),
						note: (r.note || "").trim()
					}))
				};
				setLoading(true);
				const res = await salaries_create(payload);
				consoleLogUtil("Kết quả lưu bảng lương:", res);
				if (res && res?.status === 1) {
					message.success("Lưu bảng lương thành công.");
					if (onCancel) onCancel();
				} else {
					message.error(res?.msg || "Lưu bảng lương thất bại.");
				} 
			} catch (error) {
				message.error("Lưu bảng lương thất bại.");
			} finally {
				setLoading(false);	
			}
		} else {
			// Cập nhật bảng lương
			try {
                const payloadUpdate = {
                    batch_id: dataItem?.batch?.id || dataItem?.batch_id || 0,
                    updated_by: userReducer?.user_info?.id || null,
                    salary_month: headerValues.salary_month.format("YYYY-MM"),
                    note: headerValues.note?.trim() || "",
                    total_working_days: Number(headerValues.total_working_days || 0),
                    employees: data.map(r => ({
                        detail_id: r.id || r.salary_detail_id || r.detail_id || r.key,
                        employee_id: r.employee_id,
                        working_days: Number(r.working_days || 0),
                        overtime_hours: Number(r.overtime_hours || 0),
                        late_early_hours: Number(r.late_early_hours || 0),
                        salary_base: Number(r.salary_base || 0),
                        daily_salary: Number(r.daily_salary || 0),
                        overtime_pay: Number(r.overtime_pay || 0),
                        meal_allowance: Number(r.meal_allowance || 0),
                        makeup_support: Number(r.makeup_support || 0),
                        travel_allowance: Number(r.travel_allowance || 0),
                        late_early_penalty: Number(r.late_early_penalty || 0),
                        service_bonus: Number(r.service_bonus || 0),
                        service_referral_bonus: Number(r.service_referral_bonus || 0),
                        product_referral_bonus: Number(r.product_referral_bonus || 0),
                        total_income: Number(calcTotalIncome(r)),
                        social_insurance: Number(r.social_insurance || 0),
                        training_deposit: Number(r.training_deposit || 0),
                        advance_payment: Number(r.advance_payment || 0),
                        net_salary: Number(calcNetSalary(r)),
                        note: (r.note || "").trim()
                    }))
                };
                setLoading(true);
                const res = await salaries_update(payloadUpdate);
                consoleLogUtil("Kết quả cập nhật bảng lương:", res);
                if (res && res.status === 1) {
                    message.success("Cập nhật bảng lương thành công.");
                    onCancel?.();
                } else {
                    message.error(res?.msg || "Cập nhật bảng lương thất bại.");
                }
            } catch (e) {
                message.error("Cập nhật bảng lương thất bại.");
            } finally {
                setLoading(false);
            }
		}
		
		
	}

	return (
		<Modal
			title={title || "Thêm bảng lương"}
			open={visible}
			onCancel={onCancel}
			width="95%"
			footer={null}
			destroyOnClose
		>
			<Card size="small" style={{ marginBottom: 12, borderRadius: 8 }}
			>
				<Form form={formHeader} layout="vertical">
					<Row gutter={16}>
						<Col span={6}>
							<Form.Item
								label="Tháng tính lương"
								name="salary_month"
								rules={[{ required: true, message: "Chọn tháng" }]}
							>
								<DatePicker 
									picker="month" 
									style={{ width: "100%" }} 
									format={FORMAT_DATE.MMYYYY} 
									onChange={handleChangeMonth}
									disabled={is_locked}
								/>
							</Form.Item>
						</Col>
						<Col span={6}>
							<Form.Item 
								label="Tháng này có bao nhiêu ngày công" 
								name="total_working_days" 
								initialValue={dayjs().daysInMonth() - 2}
								rules={[{ required: true, message: "Nhập số ngày công" }]}
							>
								<InputNumber
									min={0}
									style={{ width: "100%" }}
									placeholder="Nhập số ngày công trong tháng"
									disabled={is_locked}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Ghi chú" name="note">
								<TextArea 
									rows={1} 
									placeholder="Ghi chú chung bảng lương" 
									disabled={is_locked}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Card>

			<Card size="small" style={{ borderRadius: 8 }} title="Chi tiết nhân viên">
				<Form form={formRow} component={false} onValuesChange={handleRowValuesChange}>
					<Table
						components={{ body: { cell: EditableCell } }}
						bordered
						size="small"
						scroll={{ y: 800}}
						dataSource={data}
						rowKey={(r) => r.key}
						columns={mergedColumns}
						rowClassName="editable-row"
						pagination={false}
					/>
				</Form>
			</Card>

			<Row justify="end" style={{ marginTop: 16 }}>
				<Space>
					<Button icon={<CloseOutlined />} onClick={onCancel}>Đóng</Button>
					{
						is_locked ? null : (
							<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveAll}>
								Lưu bảng lương
							</Button>
						)
					}
				</Space>
			</Row>
			<LoadingBase loading={loading}/>
		</Modal>
	);
};

export default React.memo(ModalAddSalary);